/**
 * External Imports
 */
import { FC, useEffect, useState } from 'react';

/**
 * Style Imports
 */
import {
  MessageContainer,
  StyledDiv,
  StyledImage,
  SubTitle
} from './NoDataSplashView.styles';

/**
 * Static File Imports
 */
import patient from '../../../../assets/images/Patient.png';
import clock from '../../../../assets/images/Clock.png';
import leadership from '../../../../assets/images/Leadership.png';
import magnifyingGlass from '../../../../assets/images/Magnifying_Glass.png';
import sad from '../../../../assets/images/Sad.png';
import user from '../../../../assets/images/User.png';

interface NoDataSplashViewProps {
  completed?: boolean;
  search?: boolean;
  selectedTab?: string;
  type?: string;
}

const NoDataSplashView: FC<NoDataSplashViewProps> = ({
  completed = false,
  search = false,
  selectedTab,
  type = ''
}) => {
  const [imageLocation, setImageLocation] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [subTitle, setSubTitle] = useState<string>('');

  const view = window.localStorage.getItem('orbitLastPath');

  useEffect(() => {
    switch (type) {
      case 'patientCensus':
        setImageLocation(search ? sad : completed ? clock : patient);
        setTitle(
          search
            ? 'No results found'
            : completed
            ? 'No recent patient rounds'
            : 'No patients to round on'
        );
        setSubTitle(
          search
            ? 'There are no results that match your criteria'
            : completed
            ? 'We only show rounds since midnight'
            : 'This list refreshes every time you view it'
        );
        break;

      case 'directEmployeeList':
        setImageLocation(selectedTab !== 'All' ? clock : leadership);
        setTitle(
          selectedTab !== 'All'
            ? 'No recent employee rounds'
            : 'There are no employees on your list'
        );
        setSubTitle(
          selectedTab !== 'All'
            ? 'No rounds have been done in the current rounding period'
            : 'GHR does not have a record of direct reports for you.\nPlease follow up with your HR business partner.'
        );
        break;

      case 'delegateEmployeeList':
        setImageLocation(selectedTab !== 'All' ? clock : leadership);
        setTitle(
          selectedTab !== 'All'
            ? 'No recent employee rounds'
            : 'No employees to round on'
        );
        setSubTitle(
          selectedTab !== 'All'
            ? 'No rounds have been done in the current rounding period'
            : 'This list refreshes every time you view it'
        );
        break;

      case 'adHocEmployeeList':
      case 'validationList':
        setImageLocation(search ? sad : completed ? clock : leadership);
        setTitle(
          search
            ? 'No results found'
            : completed
            ? 'No recent employee rounds'
            : 'No employees to round on'
        );
        setSubTitle(
          search
            ? 'There are no results that match your criteria'
            : completed
            ? 'No rounds have been done in the current rounding period'
            : 'This list refreshes every time you view it'
        );
        break;

      case 'cnedList':
      case 'cscList':
      case 'supportList':
        setImageLocation(search ? sad : leadership);
        setTitle(search ? 'No results found' : 'No employees to round on');
        setSubTitle(
          search
            ? 'There are no results that match your criteria'
            : 'This list refreshes every time you view it'
        );
        break;

      case 'issue':
        setImageLocation(magnifyingGlass);
        setTitle(search ? 'No issues found' : 'No issues');
        setSubTitle(
          search
            ? 'There are no results that match your criteria\nCheck your spelling or remove some filtering options'
            : `There are no issues for the ${
                view === '/patients/issues' ? `unit(s)` : `employee list`
              } you have selected`
        );
        break;

      case 'feedback':
        setImageLocation(magnifyingGlass);
        setTitle(search ? 'No feedback found' : 'No feedback');
        setSubTitle(
          search
            ? 'There are no results that match your criteria\nCheck your spelling or remove some filtering options'
            : `There are no feedbacks for the ${
                view === '/patients/feedback' ? `unit(s)` : `employee list`
              } you have selected`
        );
        break;

      case 'stoplight':
        setImageLocation(magnifyingGlass);
        setTitle(search ? 'No stoplights found' : 'No stoplights');
        setSubTitle(
          search
            ? 'There are no results that match your criteria\nCheck your spelling or remove some filtering options'
            : 'There are no stoplights for the facility you have selected'
        );
        break;

      case 'discharge':
        setImageLocation(sad);
        setTitle('No results found');
        setSubTitle(
          search
            ? 'There are no results that match your criteria'
            : 'There are no patients for the facility you have selected'
        );
        break;

      case 'users':
        setImageLocation(user);
        setTitle('No users found');
        setSubTitle('This list refreshes every time you view it');
        break;

      case 'questionBank':
        setImageLocation(user);
        setTitle(search ? 'No results found' : 'No questions found');
        setSubTitle(
          search
            ? 'There are no results that match your criteria'
            : 'This list refreshes every time you view it'
        );
        break;

      case 'encounters':
        setImageLocation(user);
        setTitle('No encounters found');
        setSubTitle(
          'There are no results that match your criteria\nCheck your spelling or remove some filtering options'
        );
        break;

      case 'patientSummaryReport':
      case 'patientCensusReport':
      case 'patientComplianceReport':
      case 'patientRoundsReport':
      case 'employeeSummaryReport':
      case 'employeeRoundsReport':
      case 'employeeFrequencyReport':
        setImageLocation(sad);
        setTitle('No results found');
        setSubTitle('There are no results that match your criteria');
        break;

      case 'my-employee-list':
        setImageLocation(user);
        setTitle('There are no employees on your list');
        setSubTitle(
          'GHR does not have a record of direct reports for you\nPlease follow up with your HR business partner'
        );
        break;

      case 'delegate-list':
        setImageLocation(user);
        setTitle('You have no delegates');
        setSubTitle(
          'You can add delegates by clicking the "Add Delegates" button'
        );
        break;

      case 'delegated-to-me':
        setImageLocation(user);
        setTitle('No lists are delegated to you');
        setSubTitle('You can only be delegated to a list by the list owner');
        break;

      case 'adhoc-list': {
        setImageLocation(user);
        setTitle('There are no employees on your list');
        setSubTitle(
          'You can add employees by clicking the "Add Employees" button'
        );
        break;
      }

      case 'templates':
        setImageLocation('');
        setTitle('');
        setSubTitle('');
        break;

      default:
        setImageLocation(leadership);
        setTitle('You have reached this table in error');
    }
  }, [selectedTab, type]);

  // TODO: does this need support
  return (
    <MessageContainer id="No-Data-Splash-Page">
      <span>
        {imageLocation && (
          <StyledImage
            id="No-Data-Splash-Page-Image"
            src={imageLocation}
            alt="icon"
          />
        )}
      </span>
      <StyledDiv id="No-Data-Splash-Page-Title">
        {title}
        <SubTitle id="No-Data-Splash-Page-Sub-Title">{`${subTitle}`}</SubTitle>
      </StyledDiv>
    </MessageContainer>
  );
};

export default NoDataSplashView;
